import clsx from "clsx";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFindOneCompanyQuery } from "../../../../../features/companyApi";
import { useGetContactEmployeesQuery, useGetContactQuery } from "../../../../../features/contactsApi";
import { useFindAllRecallsQuery } from "../../../../../features/recallsApi";
import { ContactType } from "../../../../lib/filters/dto";
import useCreateBorder from "../../../../lib/hooks/createBorder";
import { keycloak } from "../../../../lib/keycloak";
import { useAppSelector } from "../../../../lib/redux/hooks";
import { useTabs } from "../../../../lib/tabValue";
import { DatabaseSection, TargetSection } from "../../../../lib/utils";
import { colors } from "../../../../ui/colors";
import { PlusIcon } from "../../../../ui/icons/plus";
import { RotateIcon } from "../../../../ui/icons/rotate";
import Avatar from "../../../../ui/molecules/avatar";
import { Spinner } from "../../../../ui/molecules/spinner";
import { TabMenu } from "../../../../ui/organisms/tabs/tabMenu";
import { TabPages } from "../../../../ui/organisms/tabs/tabPages";
import { ProjectDTO } from "../../../projects/dto";
import { EmailsMessagesDTO, FindAllCustomMailsResponseDTO } from "../../../projects/mails/dto";
import { SendEmailModal } from "../../../projects/mails/sendEmailModal";
import { SentMailsList } from "../../../projects/mails/sentMailsList";
import { ContactForm } from "../../../targetList/contacts/edit/form";
import { EditContactNotes } from "../../../targetList/contacts/edit/notes";
import { Recall } from "../../../targetList/contacts/recall";
import { ContactStateEnum, TargetListDTO } from "../../../targetList/dto";
import { CustomerAccessEnum } from "../../../users/dto";
import { CompanyEmployees } from "./employees";
import { EditCompanyForm } from "./form/editCompanyForm";

export enum CompaniesTabValue {
  form = 'form',
  employees = 'employees',
  recalls = 'recalls',
  notes = 'notes',
  emailHistory = 'emails'
}

interface Props {
  section: DatabaseSection | TargetSection
  listIndex: number
  targetList?: TargetListDTO
  project?: ProjectDTO
  emailsHistory?: { messages: EmailsMessagesDTO[] }
  templates?: FindAllCustomMailsResponseDTO
}

export function EditCompany(props: Props) {
  const { contactId, detailTab, targetContactId, projectId } = useParams()
  const navigate = useNavigate()
  const { section, targetList, project, emailsHistory, templates } = props
  const usersState = useAppSelector((state) => state.users);
  const { data: companyResponse, isLoading } = useFindOneCompanyQuery(contactId!, {
    skip: contactId === undefined
  })
  const { data: contactData, isLoading: isLoadingContact } = useGetContactQuery({ contactId: targetContactId! }, {
    skip: targetContactId === undefined
  })
  const { data: contactEmployeesData, isLoading: isLoadingEmployees } = useGetContactEmployeesQuery({ contactId: targetContactId! }, {
    skip: targetContactId === undefined
  })
  const { data: recallsData, isLoading: isLoadingRecalls } = useFindAllRecallsQuery({
    page: 0,
    pageSize: 0,
    sort: "",
    sortBy: "",
    fromDateTime: null,
    toDateTime: null,
    contactId: targetContactId!
  }, {
    skip: targetContactId === undefined
  })

  const contact = contactData
  const contactEmployees = contactEmployeesData
  const company = companyResponse?.company
  const employees = companyResponse?.employees ?? []

  const prevSectionPath = new Map<typeof section, string>([
    ["target", "/edit-target-list/" + targetList?.id + "/companies/" + projectId],
    ["database", "/database/companies"],
    ['verify-list', "/verify-bins/companies"],
    ['black-list', "/black-list/companies"],
    ['target-bin', '/target-list-bins/' + targetList?.id + '/companies/' + projectId]
  ])

  const handleClose = () => {
    navigate(prevSectionPath.get(section)!);
  }

  useEffect(() => {
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    };
    document.addEventListener('keydown', handleEscKeyPress);

    return () => {
      document.removeEventListener('keydown', handleEscKeyPress);
    };
  }, []);

  const formSection = {
    key: CompaniesTabValue.form,
    icon: <></>,
    label: "Azienda",
    page: <EditCompanyForm isInTargetList={section === "target"} company={company} key={CompaniesTabValue.form} handleClose={handleClose} />
  }

  const employeesSection = {
    key: CompaniesTabValue.employees,
    icon: <></>,
    label: "Dipendenti",
    page: <CompanyEmployees contactEmployees={contactEmployees} company={company!} section={section} employees={employees} companyId={company?.id} key={CompaniesTabValue.employees} />
  }

  const recallsSection = {
    key: CompaniesTabValue.recalls,
    icon: <RotateIcon color={""} size={0} />,
    label: "Richiami",
    page: <Recall recalls={recallsData!} project={project!} targetId={targetList?.id!} contactId={targetContactId!} title={`${company?.name ?? ''}`} contactType={ContactType.COMPANY} key={CompaniesTabValue.recalls} />
  }

  const notesSection = {
    key: CompaniesTabValue.notes,
    icon: <></>,
    label: "Note",
    page: <EditContactNotes
      contact={contact!}
      key={CompaniesTabValue.notes}
    />
  }

  const emailHistorySection = {
    key: CompaniesTabValue.emailHistory,
    icon: <></>,
    label: "Cronologia email",
    page: <SentMailsList
      templates={templates!}
      emailsHistory={
        emailsHistory?.messages?.filter(message => (
          company?.emails?.includes(message.to_email) ||
          contact?.sendEmails?.includes(message.to_email))! ||
          employees?.flatMap(emp => emp.emails).filter(email => email !== null)?.includes(message.to_email)!
        ) ?? []}
      targetList={targetList!} key={CompaniesTabValue.emailHistory} />
  }

  const tabMaps = new Map<typeof section, ({ key: CompaniesTabValue, icon: ReactElement, label: string, page: ReactElement } | null)[]>([
    ["target", [formSection, keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL ? null : employeesSection, recallsSection, notesSection, emailHistorySection]],
    ["database", [formSection, keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL ? null : employeesSection]],
    ["verify-list", [formSection, keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL ? null : employeesSection]],
    ["black-list", [formSection, keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL ? null : employeesSection]],
    ["target-bin", [formSection, keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL ? null : employeesSection, recallsSection, notesSection, emailHistorySection]],

  ])

  const tabs = tabMaps.get(section)!
  const transformed = tabs
    .filter((tab): tab is Exclude<typeof tab, null> => tab !== null)
    .map(tab => ({
      ...tab,
      key: tab.key,
    }));

  const { activeIndex, setActiveIndex } = useTabs(
    transformed,
    (detailTab as CompaniesTabValue) ?? CompaniesTabValue.form,
    (tab) => {
      const basePath = prevSectionPath.get(section)!
      return `${basePath}/${contactId}/${tab}/${targetContactId ? targetContactId : ''}`.replaceAll('//', '/')
    }
  )

  //--------------------------------------------------------------

  const borderStyle = useCreateBorder(section === "target", contact?.outcome ?? ContactStateEnum.DRAFT);

  const [direction, setDirection] = useState<"left" | "right">("left");

  return (
    <div
      className={clsx(
        "fixed w-full bg-opacity-30 bg-neutral-950 backdrop-blur-sm right-0 bottom-0 z-50 flex-shrink-0 transition-all h-full flex items-center justify-end",
        {
          hidden: contactId === undefined,
        }
      )}
      onClick={() => handleClose()}
    >
      <div className="bg-white w-[480px] shadow-md h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full overflow-auto relative">
          <div
            className={clsx(
              "absolute inset-0 z-10 bg-slate-500/20 h-full w-full flex items-center justify-center",
              {
                hidden: !(isLoading || isLoadingContact || isLoadingEmployees || isLoadingRecalls),
              })}
          >
            <Spinner />
          </div>
          <div className={borderStyle}>
            <div className="px-6 pt-4 flex items-center justify-between">
              <Avatar
                type="info"
                imageUrl=""
                firstRow={company?.name!}
                size="md"
                altTextInitials={
                  company?.name
                    .slice(0, 1)
                    .toUpperCase()!
                }
              />
              <div className="flex items-center gap-4">
                <div
                  className="cursor-pointer rotate-45"
                  onClick={() => handleClose()}
                >
                  <PlusIcon color={colors.neutral[500]} size={20} />
                </div>
              </div>
            </div>
            {
              section === "target" && <ContactForm emailsHistory={emailsHistory!} project={project!} contactEmployees={contactEmployees} contact={contact!} title={company?.name ?? ''} listIndex={props.listIndex} contactData={company} contactType={ContactType.COMPANY} />
            }
            {
              section === "verify-list" && company?.toVerifyOutcomes &&
              <div className="p-6">
                <div className="p-4 w-full rounded-lg bg-red-200 flex flex-col gap-2">
                  <span className="text-text-sm text-neutral-700 font-semibold">Esiti da verificare: </span>
                  <ul className="list-inside" style={{ listStyleType: "'-   ' " }}>
                    {company?.toVerifyOutcomes?.map((outcome, index) => (
                      <li key={index} className="text-text-sm text-neutral-700 font-medium">{outcome}</li>
                    ))}
                  </ul>
                </div>
              </div>
            }
            <TabMenu
              direction={direction}
              setDirection={(value) => setDirection(value)}
              setValue={setActiveIndex}
              value={activeIndex}
              items={tabMaps.get(section) ? tabMaps.get(section)!.filter(tab => tab !== null).map((tab, index) => ({
                icon: tab?.icon ?? <></>,
                label: tab?.label ?? "",
                index: index
              })) : []}
            />
            <div>
              <TabPages
                direction={direction}
                setDirection={(value) => setDirection(value)}
                pages={tabMaps.get(section) ? tabMaps.get(section)!.filter(tab => tab !== null).map((tab) => tab?.page ?? <></>) : []}
                setValue={setActiveIndex}
                value={activeIndex}
              />
            </div>
          </div>
          <SendEmailModal contact={contactData!} contactType={ContactType.COMPANY} project={project!} contactId={targetContactId!} company={company!} employees={employees} listIndex={-1} name={company?.name!} targetList={targetList!} />
        </div>
      </div>
    </div>
  );
}
